import { computed, ref, watch } from 'vue';
import { TranslateResult } from 'vue-i18n';

import { useStore } from './store.composable';
import { useMediaQuery } from './media-query.composable';

import { SuccessEvent } from '../models/entities/aem-forms.model';
import { VehicleTrackingInfo } from '../models/entities/tracking.model';
import {
  APP_ELEMENT_ID,
  TRACKING_EVENT_ACTION,
  TRACKING_EVENT_CATEGORY,
  TRACKING_EVENT_LABEL,
  TRACKING_EVENT_TYPE,
} from '../models/constants';

import AEMFormsService from '../services/aem-forms.service';
import { i18n } from '../services/translation.service';
import TrackingService from '../services/tracking.service';

export const useCancellation = (
  id: string,
  trackingVehicle?: VehicleTrackingInfo
) => {
  const { dispatch, getter } = useStore();
  const { isMobile } = useMediaQuery();

  const aemFormLoaded = ref(false);
  const cancellationSubmitted = ref(false);
  const cancellationSuccess = ref(false);
  const showAEMForm = ref(false);
  const showCancellationModal = ref(false);
  const testDriveId = ref('');
  const testDriveDealerId = ref('');

  // template refs
  const modalPreferences = ref(null);

  const aemFormsResources = computed((): string[] => {
    if (!getter.aemFormsResources)
      throw new Error('AEMforms endpoint not configured');
    return [getter.aemFormsResources];
  });

  const aemFormUrl = computed((): string => {
    if (!getter.aemCancellationForm)
      throw new Error('AEMforms url not configured');
    return getter.aemCancellationForm;
  });

  const appId = computed((): string => {
    return APP_ELEMENT_ID;
  });

  const modalCancellationTitle = computed((): TranslateResult | string => {
    if (cancellationSubmitted.value && !cancellationSuccess.value)
      return i18n.t('error.server.INTERNAL_SERVER_ERROR');
    if (!showAEMForm.value) return i18n.t('cancellation.headline');
    if (showAEMForm.value && cancellationSubmitted.value)
      return i18n.t('cancellation.success.title');
    return '';
  });

  const modalCancellationResponse = computed((): TranslateResult => {
    return cancellationSuccess.value
      ? i18n.t('cancellation.success.message')
      : i18n.t('error.server.INTERNAL_SERVER_ERROR');
  });

  const isFirstStepCancellation = computed((): boolean => {
    return !showAEMForm.value && !cancellationSubmitted.value;
  });

  const isLastStepCancellation = computed((): boolean => {
    return cancellationSubmitted.value;
  });

  const isLastStepDesktopCancellation = computed((): boolean => {
    return isLastStepCancellation.value && !isMobile.value;
  });

  const isLastStepMobileCancellation = computed((): boolean => {
    return isLastStepCancellation.value && isMobile.value;
  });

  const hiddenPromptFooterClass = computed((): string => {
    return (showAEMForm.value && !isLastStepCancellation.value) ||
      (isLastStepCancellation.value && !isMobile.value)
      ? 'hidden-prompt-footer'
      : '';
  });

  const getFooterClasses = computed((): string => {
    return isLastStepCancellation.value && isMobile.value
      ? 'last-step-footer'
      : hiddenPromptFooterClass.value;
  });

  const showOtbForm = computed((): boolean => {
    return !getter.cancellationFormConfig.aemForms?.enabled;
  });

  const aemFormSuccess = async (
    event: CustomEvent<SuccessEvent[]>
  ): Promise<void> => {
    const { cancellationMessage } = AEMFormsService.processCancellationSubmit(
      event,
      getter.aemFormsHandlerConfig
    );

    const cancellationReason = (
      event as any
    ).path?.[0].shadowRoot?.querySelector('[name=cancellationReason');
    const cancellationReasonText =
      cancellationReason?.options?.[cancellationReason.selectedIndex].text;

    const cancellationReasonCode =
      cancellationReason?.options?.[cancellationReason.selectedIndex].value;

    const response = await dispatch('sendForm', {
      id: testDriveId.value,
      cancellationMessage: cancellationMessage || cancellationReasonText,
    });

    if (response?.data?.cancelTestDriveBooking?.id) {
      cancellationSuccess.value = true;
      trackCancellationEvent(
        cancellationReasonCode,
        TRACKING_EVENT_ACTION.CANCELLATION_MOTIVE,
        TRACKING_EVENT_CATEGORY.FEATURE
      );
    }
    cancellationSubmitted.value = true;
    trackCancellationEvent(TRACKING_EVENT_LABEL.CANCELLATION_CONFIRM);
  };

  const leaveCancellation = (skipTracking: boolean) => {
    showAEMForm.value = false;
    showCancellationModal.value = false;
    testDriveId.value = '';
    cancellationSubmitted.value = false;
    if (!skipTracking) {
      trackCancellationEvent(TRACKING_EVENT_LABEL.CANCELLATION_LEAVE);
    }
  };

  const activateCancellationModal = (dealerId?: string) => {
    showCancellationModal.value = true;
    testDriveId.value = id;
    testDriveDealerId.value = dealerId ?? '';
    trackCancellationEvent(TRACKING_EVENT_LABEL.CANCELLATION_INIT);
  };

  const proceedCancellation = () => {
    showAEMForm.value = true;
  };

  const trackCancellationEvent = (
    label: TRACKING_EVENT_LABEL,
    action?: TRACKING_EVENT_ACTION,
    category?: TRACKING_EVENT_CATEGORY
  ): void => {
    const event = TrackingService.buildEvent(TRACKING_EVENT_TYPE.CLICK, {
      action: action || TRACKING_EVENT_ACTION.CANCELLATION,
      category: category || TRACKING_EVENT_CATEGORY.LINK,
      label,
      vehicle: trackingVehicle,
      retailerId: testDriveDealerId.value,
    });

    TrackingService.track(event);
  };

  watch(modalPreferences, (value) => {
    if (value) {
      (value as any).addEventListener('wbclose', () =>
        leaveCancellation(cancellationSuccess.value)
      );
    }
  });

  return {
    // refs
    aemFormLoaded,
    cancellationSubmitted,
    cancellationSuccess,
    showAEMForm,
    showCancellationModal,
    testDriveId,
    testDriveDealerId,
    // template refs
    modalPreferences,
    //computed
    aemFormsResources,
    aemFormUrl,
    appId,
    modalCancellationTitle,
    modalCancellationResponse,
    isFirstStepCancellation,
    isLastStepCancellation,
    isLastStepDesktopCancellation,
    isLastStepMobileCancellation,
    showOtbForm,
    // methods
    aemFormSuccess,
    activateCancellationModal,
    hiddenPromptFooterClass,
    getFooterClasses,
    leaveCancellation,
    proceedCancellation,
  };
};
